import {styled, Box, keyframes, Stack, Skeleton} from '@mui/material';
import React from 'react';
import DefaultVoteIcon from '../../assets/default-vote-icon.svg';
import useSubmitVote from '../../hooks/useSubmitVote';
import {VoteEnum} from '../../types';
import {OutlinedButton} from './OutlinedButton';
import {TalkingPointDescription} from './TalkingPointDescription';
import {TalkingPointHeading} from './TalkingPointHeading';
import {TalkingPointLabel} from './TalkingPointLabel';
import TalkingPointPopup from './TalkingPointPopup';
import editIcon from '../../assets/edit-icon.png';
import VoteLabel from './VoteLabel';
import {getVoteType} from '../../utils/misc';
import VoteIcon from './VoteIcon';

type TalkingPointProps = {
  data?: {
    id: string;
    title: string;
    description: string;
    vote: string;
  };
  active?: boolean;
  onClick?: () => void;
  skeleton?: boolean;
  PopupProps?: {
    hideBackdrop: boolean;
    paperCoords: {
      left: number;
      width: number;
    };
  };
};

const Container = styled('div')(({theme}) => ({
  position: 'relative',
  background: 'rgba(255, 255, 255, 0.94)',
  backdropFilter: 'blur(5px)',
  padding: theme.spacing(2),
  border: '1px solid #FFFFFF41',
  borderRadius: theme.typography.pxToRem(16),
  '::before': {
    content: '""',
    position: 'absolute',
    top: -20,
    left: 40,
    transform: 'translate(-50%, -50%)',
    width: theme.typography.pxToRem(10),
    height: theme.typography.pxToRem(10),
    borderRadius: '50%',
    backgroundColor: '#545454',
    zIndex: 1,
  },
}));

const colorAnimation = keyframes`
  from {background-color: rgba(255, 0, 0, 0.4);}
  to {background-color: rgba(255, 0, 0, 0.5);}
`;

const ActiveIcon = styled('div')(({theme}) => ({
  width: '1rem',
  height: '1rem',
  background: 'rgba(255, 0, 0, 0.4)',
  borderRadius: '50%',
  filter: ' blur(10px)',
  animation: `${colorAnimation} 1s ease-in-out infinite alternate`,
}));

const Button = styled(OutlinedButton)(({theme}) => ({
  borderRadius: theme.typography.pxToRem(16),
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
  padding: `${theme.typography.pxToRem(6)} ${theme.typography.pxToRem(16)}`,
  width: 'fit-content',
}));

function TalkingPoint({data, active = false, skeleton, PopupProps}: TalkingPointProps) {
  const [open, setOpen] = React.useState(false);
  const vote = useSubmitVote();

  const onSubmit = React.useCallback(
    (tpId: string, type: VoteEnum) => {
      vote(tpId, type);
      setOpen(false);
    },
    [vote, setOpen]
  );

  const onClick = () => {
    setOpen(true);
  };
  return (
    <Container>
      {active && (
        <TalkingPointLabel
          sx={{
            position: 'absolute',
            zIndex: 1,
            top: '-20px',
            left: '55px',
            transform: 'translateY(-50%)',
          }}
        >
          Current talking point
        </TalkingPointLabel>
      )}
      <Stack direction="row" justifyContent="space-between">
        <TalkingPointHeading>
          {!skeleton && (
            <>
              {data?.id}. {data?.title}
            </>
          )}
          {skeleton && <Skeleton variant="text" width={60} />}
        </TalkingPointHeading>
        {active && <ActiveIcon />}
      </Stack>

      <TalkingPointDescription>
        {!skeleton && <>{data?.description}</>}
        {skeleton && <Skeleton variant="text" width="100%" />}
      </TalkingPointDescription>
      {data && data.vote === '' && (
        <OutlinedButton disabled={skeleton} onClick={onClick}>
          <Box component="img" src={DefaultVoteIcon} alt="default vote" />
        </OutlinedButton>
      )}
      {data && data.vote !== '' && (
        <Stack direction="row" justifyContent="space-between">
          <VoteLabel type={getVoteType(data.vote)}>
            <VoteIcon type={getVoteType(data.vote)} /> {data.vote}
          </VoteLabel>
          <Button onClick={onClick}>
            Change vote{' '}
            <Box
              component="img"
              src={editIcon}
              alt="edit"
              sx={{width: '12px', height: '12px', objectFit: 'contain', ml: 2}}
            />
          </Button>
        </Stack>
      )}
      {data && (
        <TalkingPointPopup
          open={open}
          hideBackdrop={!!PopupProps?.hideBackdrop}
          paperCoords={PopupProps?.paperCoords || {left: 0, width: 0}}
          onClose={() => setOpen(false)}
          data={data}
          onSubmit={onSubmit}
        />
      )}
    </Container>
  );
}

export default TalkingPoint;
